import React, { useRef } from "react";
import { schoolPrompt, publicWarnings } from "../import/findings-display";
import { INTENTS } from "../import/intent";
import SpreadsheetDropzone from "./SpreadsheetDropzone";
import IntentChooser from "./understand/IntentChooser";
import ExistingChooser from "./understand/ExistingChooser";
import SchoolDecision from "./understand/SchoolDecision";
import PassengerTable from "./understand/PassengerTable";
import ColumnMapper from "./understand/ColumnMapper";
import RouteListCard from "./understand/RouteListCard";

const plural = (count, one, many) => `${count} ${count === 1 ? one : many || `${one}s`}`;

const SampleUnderstand = ({ fileName, parsed, preview, onNext, onBack, canAdvance, onFile, replaceError, replaceBusy }) => {
  const stats = preview || parsed.stats || {};
  return (
    <section className="start-stage">
      <header className="start-stage__header">
        <div className="start-stage__header-row">
          <p className="start-kicker">Sample roster</p>
          {onFile && (
            <SpreadsheetDropzone variant="button" onFile={onFile} error={replaceError} busy={replaceBusy} buttonLabel="Upload my own file" />
          )}
        </div>
        <h2>What we found in {fileName || "the spreadsheet"}</h2>
        <p>A synthetic three-school roster with a few gaps left in on purpose, so you can see how RouteBot surfaces them.</p>
      </header>
      <div className="start-stat-row">
        <article>
          <strong>{stats.students?.length ?? parsed.rows?.length ?? 0}</strong>
          <span>Rows</span>
        </article>
        <article>
          <strong>{stats.schools?.length ?? 0}</strong>
          <span>Schools</span>
        </article>
        <article>
          <strong>{stats.recognizedCount ?? 0}</strong>
          <span>Named passengers</span>
        </article>
        <article>
          <strong>{stats.missingAddressCount ?? 0}</strong>
          <span>Missing addresses</span>
        </article>
      </div>
      <div className="start-table-wrap">
        <table className="start-table">
          <thead>
            <tr>
              {parsed.headers.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parsed.rows.slice(0, 8).map((row, index) => (
              <tr key={`preview-row-${index}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${index}-${cellIndex}`}>{cell || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {parsed.rows.length > 8 && <p className="start-muted">Showing the first 8 of {parsed.rows.length} rows.</p>}
      </div>
      <div className="start-stage__nav">
        <button type="button" className="start-btn start-btn--ghost" onClick={onBack}>
          Back
        </button>
        <button type="button" className="start-btn" onClick={onNext} disabled={canAdvance === false}>
          Review the operation
        </button>
      </div>
    </section>
  );
};

const StageUnderstand = (props) => {
  const {
    fileName,
    parsed,
    mapping,
    source,
    extraction,
    schoolName,
    schoolAddress,
    schoolDecision,
    onSchoolName,
    onSchoolAddress,
    onSchoolDecision,
    onNext,
    onBack,
    onFile,
    replaceError,
    replaceBusy,
    canAdvance,
    intent,
    intentSuggestion,
    onIntent,
    includeRouteList,
    onIncludeRouteList,
    existingMode,
    onExistingMode,
    existingDecisions,
    onExistingDecision,
    match,
    matchBusy,
    isLoggedIn,
    onUpdatePassenger,
    onRemovePassenger,
    onRemoveRows,
    undoStack,
    onUndo,
    onApplyMapping,
  } = props;
  const schoolPanelRef = useRef(null);
  if (!parsed) return null;
  if (source !== "upload") return <SampleUnderstand {...props} />;

  const stats = extraction?.stats || {};
  const passengerCount = stats.passengerCount || 0;
  const routeList = extraction?.routes || [];
  const prompt = extraction ? schoolPrompt(extraction) : null;
  const warnings = publicWarnings(extraction);
  const continueEnabled = canAdvance != null ? canAdvance : passengerCount > 0;
  const routesOnly = intent === INTENTS.ROUTES_ONLY;
  const schoolMissing = prompt?.status === "missing" && (passengerCount > 0 || routeList.length > 0);
  const schoolNeeded = schoolMissing && (!routesOnly || passengerCount === 0);
  const schoolSatisfied =
    schoolDecision === "later" ||
    ((schoolDecision === "create" || schoolDecision === "existing") && Boolean(String(schoolName || "").trim()));
  const schoolBlocked = schoolNeeded && !schoolSatisfied;
  const nothingFound = passengerCount === 0 && routeList.length === 0;
  const parentNamed = (extraction?.passengers || []).filter((passenger) => (passenger.parents || []).some((parent) => parent.name)).length;
  const phones = (extraction?.passengers || []).filter(
    (passenger) => (passenger.parents || []).some((parent) => parent.phone) || passenger.phone,
  ).length;
  const existing = match?.summary?.existing || 0;

  const continueHint = nothingFound
    ? "No passenger names were found in this file."
    : schoolBlocked
      ? schoolDecision === "create"
        ? "Enter a school name above — Continue stays off until you do."
        : "Choose how to handle the school above — Continue stays off until you do."
      : !intent
        ? "Choose what to do with this file above."
        : null;

  const continueLabel = routesOnly ? "Review the services" : intent === INTENTS.PASSENGERS ? "Review and save" : "Continue";

  return (
    <section className="start-stage">
      <header className="start-stage__header">
        <div className="start-stage__header-row">
          <p className="start-kicker">Your file</p>
          {onFile && (
            <SpreadsheetDropzone variant="button" onFile={onFile} error={replaceError} busy={replaceBusy} buttonLabel="Upload a different file" />
          )}
        </div>
        <h2>What we found in {fileName || "the spreadsheet"}</h2>
        <p className="start-summary">
          {passengerCount > 0 && <span>{plural(passengerCount, "passenger")}</span>}
          {parentNamed > 0 && <span>{plural(parentNamed, "parent name")}</span>}
          {phones > 0 && <span>{plural(phones, "phone")}</span>}
          {stats.schoolCount > 0 && <span>{plural(stats.schoolCount, "school")}</span>}
          {stats.pickupRouteNameCount > 0 && <span>{plural(stats.pickupRouteNameCount, "morning service")}</span>}
          {stats.dropoffRouteNameCount > 0 && <span>{plural(stats.dropoffRouteNameCount, "afternoon service")}</span>}
          {stats.distinctRouteNameCount === 0 && stats.routeListCount > 0 && <span>{plural(stats.routeListCount, "service")} listed</span>}
          {isLoggedIn && existing > 0 && <span className="start-summary__hint">{existing} already registered</span>}
          {parsed.sheetNames?.length > 1 && <span className="start-muted">{parsed.sheetNames.length} sheets read</span>}
        </p>
      </header>

      {warnings.length > 0 && (
        <ul className="start-warnings">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      {nothingFound && parsed.headers?.length > 0 && (
        <ColumnMapper headers={parsed.headers} initialMapping={mapping || {}} onApply={onApplyMapping} />
      )}

      {!nothingFound && (
        <IntentChooser
          suggestion={intentSuggestion}
          intent={intent}
          onChange={onIntent}
          includeRouteList={includeRouteList}
          onIncludeRouteList={onIncludeRouteList}
          routeList={routeList}
          isLoggedIn={isLoggedIn}
        />
      )}

      {isLoggedIn && !nothingFound && (
        <ExistingChooser match={match} intent={intent} mode={existingMode} onChange={onExistingMode} busy={matchBusy} />
      )}

      {schoolMissing && (
        <SchoolDecision
          ref={schoolPanelRef}
          prompt={prompt}
          schoolName={schoolName}
          schoolAddress={schoolAddress}
          schoolDecision={schoolDecision}
          onSchoolName={onSchoolName}
          onSchoolAddress={onSchoolAddress}
          onSchoolDecision={onSchoolDecision}
          blocked={schoolBlocked}
          companySchools={match?.company?.schools || []}
          required={!routesOnly || passengerCount > 0}
        />
      )}
      {prompt?.status === "found" && (
        <SchoolDecision
          ref={schoolPanelRef}
          prompt={prompt}
          schoolName={schoolName}
          schoolAddress={schoolAddress}
          schoolDecision={schoolDecision}
          onSchoolName={onSchoolName}
          onSchoolAddress={onSchoolAddress}
          onSchoolDecision={onSchoolDecision}
          blocked={false}
        />
      )}

      {(passengerCount === 0 || stats.routeListWithoutPassengers > 0) && <RouteListCard routes={routeList} passengerCount={passengerCount} />}

      {(passengerCount > 0 || (extraction?.passengers || []).length > 0) && (
        <PassengerTable
          extraction={extraction}
          match={match}
          intent={intent}
          existingMode={existingMode}
          existingDecisions={existingDecisions}
          onUpdate={onUpdatePassenger}
          onRemove={onRemovePassenger}
          onRemoveMany={onRemoveRows}
          onDecision={onExistingDecision}
          undoStack={undoStack}
          onUndo={onUndo}
          isLoggedIn={isLoggedIn}
        />
      )}

      <div className="start-stage__nav">
        <button type="button" className="start-btn start-btn--ghost" onClick={onBack}>
          Back
        </button>
        <div className="start-stage__nav-end">
          {continueHint && (
            <p className="start-nav-hint" id="start-continue-hint">
              {continueHint}{" "}
              {schoolBlocked && (
                <button
                  type="button"
                  className="start-text-link"
                  onClick={() => schoolPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                >
                  Go to school options
                </button>
              )}
            </p>
          )}
          <button
            type="button"
            className="start-btn"
            onClick={onNext}
            disabled={!continueEnabled}
            aria-describedby={continueHint ? "start-continue-hint" : undefined}
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </section>
  );
};

export default StageUnderstand;
